import { useEffect, useState } from "react";
import axios from "axios";

const API = `${window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL}/api`;
const COLS = [
  ["estimate_started", "Started"], ["estimate_completed", "Completed"], ["booked_online", "Booked online"],
  ["booked_any", "Any booking"], ["paid_estimates", "Paid"],
];

const Diff = ({ d, p }) => {
  if (d === undefined) return null;
  const sig = p !== null && p !== undefined && p < 0.05;
  return (
    <span className={`block text-[10px] ${d > 0 ? "text-emerald-700" : d < 0 ? "text-red-600" : "text-zinc-400"}`}>
      {d > 0 ? "+" : ""}{d} pts{p !== undefined && <> · p={p === null ? "–" : p}{sig && " ★"}</>}
    </span>
  );
};

function ExperimentReconciliation({ r }) {
  const reasons = Object.entries(r.untagged_by_reason || {});
  return (
    <div className="text-xs bg-zinc-50 border border-zinc-200 rounded-lg p-3 space-y-1" data-testid="exp-reconciliation">
      <p data-testid="exp-recon-summary">
        <strong>Check vs Funnel Analytics:</strong> {r.funnel_sessions} funnel sessions · {r.tagged_sessions} tagged with this test
        ({r.coverage_pct}%) · {r.counted_sessions} counted · {r.staff_sessions} staff · {r.qa_sessions} QA · {r.experiment_visitors} unique visitors
      </p>
      {reasons.length > 0 && (
        <p className="text-zinc-600" data-testid="exp-recon-untagged">
          Not tagged ({r.untagged_sessions}): {reasons.map(([k, v]) => `${v} ${k}`).join(" · ")}
        </p>
      )}
    </div>
  );
}

const LABELS = {
  completed_without_start: "completed without a start",
  multiple_variants: "events with more than one variant",
  completion_without_estimate: "completion with no linked estimate",
};

function ExperimentIntegrity({ i }) {
  const issues = Object.entries(i.by_variant || {}).flatMap(([v, checks]) =>
    Object.entries(checks).filter(([, x]) => x.count > 0).map(([k, x]) => ({ v, k, x })));
  return (
    <div className={`text-xs rounded-lg p-3 border ${i.total_issues ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`} data-testid="exp-integrity">
      <p data-testid="exp-integrity-summary">
        <strong>Integrity:</strong> {i.total_issues ? `${i.total_issues} issue(s)` : "no issues"} · {i.unlinked_estimates?.count || 0} unlinked estimates · {i.resumed_events_excluded || 0} resume-link events excluded
      </p>
      {issues.map(({ v, k, x }) => (
        <p key={`${v}-${k}`} className="text-zinc-700 mt-1 break-all" data-testid={`exp-integrity-${v}-${k}`}>
          {v}: {x.count} {LABELS[k]} — {x.visitor_ids.join(", ")}
        </p>
      ))}
    </div>
  );
}

export function HeadlineExperimentResults({ expKey, refreshKey }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  useEffect(() => {
    const q = days === "today" ? "start_date=today" : `days=${days}`;
    axios.get(`${API}/reports/experiments/${expKey}?${q}`,
      { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } })
      .then((r) => setData(r.data)).catch(() => setData({ variants: [] }));
  }, [expKey, days, refreshKey]);

  return (
    <div className="space-y-3" data-testid="headline-experiment-results">
      <div className="flex items-center gap-2">
        <h3 className="font-semibold text-zinc-900">Results</h3>
        <select value={days} data-testid="exp-results-days"
          onChange={(e) => setDays(e.target.value === "today" ? "today" : Number(e.target.value))}
          className="ml-auto text-sm border border-zinc-300 rounded-md px-2 py-1 bg-white">
          <option value="today">Today</option>
          {[7, 14, 30, 60, 90, 180, 365].map((d) => <option key={d} value={d}>Last {d} days</option>)}
        </select>
      </div>
      {data?.key && (
        <p className="text-[11px] text-zinc-500" data-testid="exp-results-window">
          Experiment {data.experiment_start_at ? `started ${new Date(data.experiment_start_at).toLocaleString()}` : "has no start"}
          {data.experiment_end_at ? `, ends ${new Date(data.experiment_end_at).toLocaleString()}` : ", no end date"} · booking window {data.book_window_days} days
        </p>
      )}
      {data?.reconciliation && <ExperimentReconciliation r={data.reconciliation} />}
      {data?.integrity && <ExperimentIntegrity i={data.integrity} />}
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="exp-results-table">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-zinc-500 border-b">
              <th className="py-2 pr-3">Variant</th><th className="py-2 pr-3">Visitors</th>
              <th className="py-2 pr-3 whitespace-nowrap">Page views</th>
              {COLS.map(([, l]) => <th key={l} className="py-2 pr-3 whitespace-nowrap">{l}</th>)}
              <th className="py-2 pr-3">Paid inv.</th><th className="py-2 pr-3">Revenue</th>
            </tr>
          </thead>
          <tbody>
            {(data?.variants || []).map((r) => (
              <tr key={r.variant} className="border-b border-zinc-100 align-top" data-testid={`exp-row-${r.variant}`}>
                <td className="py-2 pr-3"><strong>{r.variant}</strong>
                  <span className="block text-[11px] text-zinc-500 max-w-[180px] truncate" title={r.headline}>{r.headline}</span></td>
                <td className="py-2 pr-3 font-medium" data-testid={`exp-visitors-${r.variant}`}>{r.visitors}</td>
                <td className="py-2 pr-3" data-testid={`exp-pageviews-${r.variant}`}>{r.page_views}</td>
                {COLS.map(([k]) => (
                  <td key={k} className="py-2 pr-3 whitespace-nowrap" data-testid={`exp-${k}-${r.variant}`}>
                    {r[k]} <span className="text-zinc-500">({r.rates[k]}%)</span>
                    <Diff d={r.diff_vs_control[k]} p={r.p_values[k]} />
                  </td>
                ))}
                <td className="py-2 pr-3">{r.paid_invoices}</td>
                <td className="py-2 pr-3">${r.revenue.toLocaleString()}<span className="block text-[10px] text-zinc-500">${r.revenue_per_visitor}/visitor</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-zinc-500">
        Rates are per unique visitor (cookie). Differences are vs control A; p-values from a two-proportion z-test (★ = p&lt;0.05).
        Excludes QA links (?sz_variant=) and browsers logged into the CRM.
      </p>
    </div>
  );
}
